
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/base/Button';

export default function ProductsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCards, setSelectedCards] = useState<{[key: string]: number[]}>({});
  const sectionRef = useRef<HTMLElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // 检查是否为移动设备
  const isMobile = () => window.innerWidth < 768;

  // 初始化默认选中状态（仅移动端）
  useEffect(() => {
    if (isMobile()) {
      setSelectedCards({
        'proof_series': [0, 1], // 选中所有卡片
        'declaration_series': [0, 1] // 选中所有卡片
      });
    }

    const handleResize = () => {
      if (isMobile()) {
        setSelectedCards({
          'proof_series': [0, 1],
          'declaration_series': [0, 1]
        });
      } else {
        setSelectedCards({});
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCardClick = (seriesKey: string, productIndex: number) => {
    if (isMobile()) {
      setSelectedCards(prev => {
        const currentSelected = prev[seriesKey] || [];
        const isSelected = currentSelected.includes(productIndex);
        
        if (isSelected) {
          // 取消选中
          return {
            ...prev,
            [seriesKey]: currentSelected.filter(index => index !== productIndex)
          };
        } else {
          // 选中
          return {
            ...prev,
            [seriesKey]: [...currentSelected, productIndex]
          };
        }
      });
    }
  };

  const productSeries = [
    {
      key: 'proof_series',
      title: t('proof_series'),
      subtitle: t('proof_series_subtitle'),
      description: t('proof_series_description'),
      products: [
        {
          name: t('acai_powder'),
          description: t('acai_description'),
          image: "https://public.readdy.ai/ai/img_res/abd010d6a073bbc352ff3b7e89de058c.jpg"
        },
        {
          name: t('white_tomato'),
          description: t('white_tomato_description'),
          image: "https://readdy.ai/api/search-image?query=White%20tomato%20powder%20in%20premium%20minimalist%20packaging%2C%20luxury%20health%20supplement%20product%20photography%2C%20clean%20white%20background%2C%20elegant%20modern%20design%2C%20soft%20studio%20lighting%2C%20high-end%20wellness%20product%20aesthetic%2C%20sophisticated%20branding&width=400&height=400&seq=white-tomato&orientation=squarish"
        }
      ]
    },
    {
      key: 'declaration_series',
      title: t('declaration_series'),
      subtitle: t('declaration_series_subtitle'),
      description: t('declaration_series_description'),
      products: [
        {
          name: t('glow_booster'),
          description: t('glow_description'),
          image: "https://readdy.ai/api/search-image?query=Glow%20Booster%20supplement%20in%20sleek%20modern%20packaging%2C%20premium%20wellness%20product%20photography%2C%20minimalist%20white%20background%2C%20luxury%20health%20supplement%20design%2C%20soft%20natural%20lighting%2C%20elegant%20branding%2C%20sophisticated%20aesthetic&width=400&height=400&seq=glow-booster&orientation=squarish"
        },
        {
          name: t('energy_elixir'),
          description: t('energy_description'),
          image: "https://readdy.ai/api/search-image?query=Energy%20Elixir%20supplement%20in%20premium%20modern%20packaging%2C%20luxury%20wellness%20product%20photography%2C%20clean%20white%20background%2C%20elegant%20minimalist%20design%2C%20soft%20studio%20lighting%2C%20high-end%20health%20product%20aesthetic%2C%20sophisticated%20branding&width=400&height=400&seq=energy-elixir&orientation=squarish"
        }
      ]
    }
  ];

  return (
    <section id="products" ref={sectionRef} className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-100 translate-y-0'}`}>
          <h2 
            className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#2A2A2A] mb-6"
            style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
          >
            {t('products_title')}
          </h2>
          <p 
            className="text-xs sm:text-xs lg:text-base text-[#A6A6A6] max-w-3xl mx-auto"
            style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
          >
            {t('products_subtitle')}
          </p>
        </div>

        {/* Product Series */}
        <div className="space-y-24">
          {productSeries.map((series, seriesIndex) => (
            <div 
              key={seriesIndex}
              className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-100 translate-y-0'}`}
            >
              {/* Series Header */}
              <div className="text-center mb-12">
                <h3 
                  className="text-lg sm:text-xl lg:text-3xl font-bold text-[#2A2A2A] mb-4"
                  style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
                >
                  {series.title}
                </h3>
                <p 
                  className="text-xs sm:text-sm lg:text-lg text-[#A6A6A6] mb-6"
                  style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                >
                  {series.subtitle}
                </p>
                <p 
                  className="text-xs leading-relaxed max-w-4xl mx-auto"
                  style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                >
                  {series.description}
                </p>
              </div>

              {/* Products Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16" data-product-shop>
                {series.products.map((product, productIndex) => {
                  const isSelected = selectedCards[series.key]?.includes(productIndex) || false;
                  const shouldShowSelected = isMobile() && isSelected;
                  
                  return (
                    <div 
                      key={productIndex}
                      className="group cursor-pointer"
                      onClick={() => handleCardClick(series.key, productIndex)}
                    >
                      <div className={`rounded-lg p-6 md:p-8 transition-all duration-300 ${
                        shouldShowSelected 
                          ? 'bg-[#F5F1E6]/60 shadow-xl ring-2 ring-[#F5F1E6] ring-opacity-50' 
                          : 'bg-[#F5F1E6]/20 hover:bg-[#F5F1E6]/40 hover:shadow-lg'
                      }`}>
                        {/* Product Image */}
                        <div className="mb-6">
                          <img 
                            src={product.image}
                            alt={product.name}
                            className="w-full h-64 md:h-80 lg:h-96 object-cover object-center rounded-lg"
                          />
                        </div>

                        {/* Product Info */}
                        <div className="space-y-4">
                          <h4 
                            className="text-sm sm:text-base lg:text-xl font-semibold text-[#2A2A2A]"
                            style={{ fontFamily: 'Playfair Display, Noto Serif SC, serif' }}
                          >
                            {product.name}
                          </h4>
                          <p 
                            className="text-xs text-[#A6A6A6]"
                            style={{ fontFamily: 'Inter, Noto Sans SC, sans-serif' }}
                          >
                            {product.description}
                          </p>
                          <div className="pt-4">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className={`text-xs transition-all duration-300 ${
                                shouldShowSelected 
                                  ? 'bg-[#2A2A2A] text-white hover:bg-[#1A1A1A]' 
                                  : ''
                              }`}
                            >
                              {t('learn_more')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
